import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, Shield, MapPin, Phone, Users, Eye, AlertTriangle,
  Heart, MessageSquare, Lock, UserCheck, Coffee, Ban, Flag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const SafetyTip = ({ icon: Icon, title, description, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

const SafetySection = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
      <Shield className="w-5 h-5" />
      {title}
    </h2>
    <div className="space-y-3">
      {children}
    </div>
  </div>
);

const SafetyTipsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="safety-tips-page">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#1C3A7A] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-[#FAB710]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1C3A7A] mb-3">Safety Tips</h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Your safety is our priority. Follow these guidelines to have a secure and positive experience on RecoveryMatch.
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Emergency Banner */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 mb-1">In Crisis or Danger?</h3>
              <p className="text-sm text-red-700 mb-2">
                If you're in immediate danger, contact emergency services (911). For substance abuse support:
              </p>
              <p className="text-sm font-medium text-red-800">
                SAMHSA Helpline: 1-800-662-4357 (24/7, free, confidential)
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-10">
          {/* Online Safety */}
          <SafetySection title="Online Safety">
            <SafetyTip
              icon={Lock}
              title="Protect Your Personal Information"
              description="Never share your home address, workplace, financial info, or daily routines with someone you haven't met. Keep your sobriety date private until you're comfortable."
              color="blue"
            />
            <SafetyTip
              icon={Eye}
              title="Stay on the Platform"
              description="Keep conversations on RecoveryMatch until you feel safe. Scammers often try to move to other apps quickly. Our chat is monitored for your protection."
              color="blue"
            />
            <SafetyTip
              icon={UserCheck}
              title="Look for Verified Profiles"
              description="Verified profiles have confirmed their identity with a selfie. Look for the verification badge when browsing matches."
              color="green"
            />
            <SafetyTip
              icon={Ban}
              title="Block & Report Suspicious Users"
              description="Trust your instincts. If someone makes you uncomfortable, block them immediately. Report inappropriate behavior to help keep the community safe."
              color="red"
            />
          </SafetySection>

          {/* Meeting In Person */}
          <SafetySection title="Meeting In Person">
            <SafetyTip
              icon={Coffee}
              title="Meet in Public Places"
              description="Always meet in well-lit, public locations like coffee shops or restaurants. Avoid isolated areas and never invite someone to your home on a first date."
              color="amber"
            />
            <SafetyTip
              icon={Users}
              title="Tell Someone Your Plans"
              description="Share your date details with a trusted friend or family member. Include the person's name, where you're meeting, and when you expect to be back."
              color="amber"
            />
            <SafetyTip
              icon={Phone}
              title="Arrange Your Own Transportation"
              description="Drive yourself or use rideshare. Don't rely on your date for transportation—this ensures you can leave whenever you want."
              color="amber"
            />
            <SafetyTip
              icon={MapPin}
              title="Stay Sober on First Dates"
              description="As someone in recovery, you understand the importance of clear judgment. Keep your first few dates in environments that support your sobriety."
              color="green"
            />
          </SafetySection>

          {/* Recovery-Specific Safety */}
          <SafetySection title="Recovery-Specific Safety">
            <SafetyTip
              icon={Heart}
              title="Respect Each Other's Recovery"
              description="Everyone's recovery journey is unique. Never pressure someone about their program, meeting attendance, or sobriety length. Support, don't judge."
              color="purple"
            />
            <SafetyTip
              icon={MessageSquare}
              title="Set Boundaries Early"
              description="It's okay to discuss your boundaries about substances, triggers, and what you need in a relationship. Clear communication builds healthier connections."
              color="purple"
            />
            <SafetyTip
              icon={AlertTriangle}
              title="Watch for Red Flags"
              description="Be cautious of people who dismiss your recovery, pressure you, ask for money, refuse video calls, or have inconsistent stories about themselves."
              color="red"
            />
            <SafetyTip
              icon={Flag}
              title="Prioritize Your Recovery"
              description="Your sobriety comes first. If a relationship threatens your recovery, it's okay to step back. Use your support network when needed."
              color="green"
            />
          </SafetySection>

          {/* Resources */}
          <div className="bg-[#1C3A7A]/5 rounded-xl p-6 mt-8">
            <h3 className="font-bold text-[#1C3A7A] mb-4">Helpful Resources</h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FAB710] rounded-full flex-shrink-0"></span>
                <span><strong>SAMHSA National Helpline:</strong> 1-800-662-4357</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FAB710] rounded-full flex-shrink-0"></span>
                <span><strong>National Domestic Violence Hotline:</strong> 1-800-799-7233</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FAB710] rounded-full flex-shrink-0"></span>
                <span><strong>Crisis Text Line:</strong> Text HOME to 741741</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FAB710] rounded-full flex-shrink-0"></span>
                <span><strong>AA Meeting Finder:</strong> aa.org/find-aa</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FAB710] rounded-full flex-shrink-0"></span>
                <span><strong>NA Meeting Search:</strong> na.org/meetingsearch</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-3">
              See something concerning? Let us know.
            </p>
            <Button 
              onClick={() => navigate("/support")}
              className="bg-[#FAB710] hover:bg-[#e5a60f] text-white"
            >
              Contact Support
            </Button>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button 
            onClick={() => navigate("/")}
            className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition"
          >
            ← Back to Home
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SafetyTipsPage;
